'use client';

import { createTopic } from '@/actions/forum';
import { Button } from '@ui/primitives';
import { Dropdown, Input } from '@ui/primitives';
import { Alert } from '@ui/composites';
import { useTranslation } from '@/lib';
import { event } from '@/lib/analytics/gtag';
import { hasPermission, UserRole } from '@/lib/auth/permissions';
import { UserProfile } from '@/types/users';
import { useRouter } from 'next/navigation';
import { useActionState, useEffect, useState } from 'react';

interface CreateTopicFormProps {
    profile: UserProfile | null;
    forumId: string;
    categoryId: string;
    slug: string;
    locale: string;
}

export default function CreateTopicForm({ profile, forumId, categoryId, slug, locale }: CreateTopicFormProps) {
    const { t, ts } = useTranslation();
    const router = useRouter();
    const [state, formAction, isPending] = useActionState(createTopic, undefined);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [type, setType] = useState('normal');

    useEffect(() => {
        if (state && !state.isError && state.data) {
            event({ action: 'create_topic', category: 'forum', label: String(forumId) });
            setTitle('');
            setContent('');
            setType('normal');
            // Redirect to the new topic
            const topicSlug = state.data.title?.toLowerCase().replace(/\s+/g, '-') || 'topic';
            router.push(`/${locale}/forum/${categoryId}/${forumId}/topic/${state.data.id}/${topicSlug}`);
        }
    }, [state, router, locale, categoryId, forumId]);

    if (!profile || !hasPermission(profile.role, UserRole.MEMBER)) {
        return null;
    }

    return (
        <section className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t('forum.create_topic')}
            </h2>
            
            <form action={formAction}>
                <input type="hidden" name="forum_id" value={forumId} />
                
                <div className="space-y-4">
                    {state?.message && (
                        <Alert 
                            variant={state.isError ? 'error' : 'success'}
                        >
                            {state.message}
                        </Alert>
                    )}

                    <div className="grid grid-cols-12 gap-4">
                        <div className={hasPermission(profile.role, UserRole.MODERATOR) ? "col-span-8" : "col-span-12"}>
                            <Input
                                id="title"
                                name="title"
                                type="text"
                                placeholder={ts('forms.title_placeholder')}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                                disabled={isPending}
                            />
                        </div>

                        {hasPermission(profile.role, UserRole.MODERATOR) && (
                            <div className="col-span-4">
                                <Dropdown
                                    id="type"
                                    value={type}
                                    placeholder={String(t('generic.type'))}
                                    onChange={(e) => setType(e.value)}
                                    options={[
                                        { label: String(t('forum.announces_title')), value: 'announce' },
                                        { label: String(t('forum.topics_title')), value: 'normal' }
                                    ]}
                                    disabled={isPending}
                                />
                            </div>
                        )}
                    </div>

                    <div>
                        <textarea
                            id="content"
                            name="content"
                            placeholder={ts('forms.content_placeholder')}
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            required
                            disabled={isPending}
                            rows={8}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                        />
                    </div>

                    <Button type="submit" disabled={isPending}>
                        {isPending ? t('common.loading') : t('actions.submit')}
                    </Button>
                </div>
            </form>
        </section>
    );
}
